import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { OTHER_INCOME_TYPES } from "@/lib/wallet/incomeCategories";
import type { AnyRow } from "@/lib/rowTypes";

export interface OtherIncomeItem {
  id: string;
  amount: number;
  type: string;
  description: string | null;
  source: string | null;
  createdAt: string;
}

/**
 * Reads non-career income (allowance, gift, parent_bonus, module_reward) from
 * account_transactions. Independent of useWeeklyPaycheck / careerEligibility.
 */
export function useOtherIncome(limit = 10) {
  const { user } = useAuth();
  const [items, setItems] = useState<OtherIncomeItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    if (!user) {
      setItems([]);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("account_transactions")
      .select("id, amount, transaction_type, description, metadata, created_at")
      .eq("user_id", user.id)
      .in("transaction_type", [...OTHER_INCOME_TYPES])
      .order("created_at", { ascending: false })
      .limit(limit);

    const rows: OtherIncomeItem[] = (data ?? []).map((row: AnyRow) => ({
      id: row.id,
      amount: Number(row.amount) || 0,
      type: row.transaction_type,
      description: row.description,
      source: row.metadata?.source ?? null,
      createdAt: row.created_at,
    }));
    setItems(rows);
    setLoading(false);
  }, [user, limit]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const totalThisMonth = (() => {
    const now = new Date();
    return items
      .filter((it) => {
        const d = new Date(it.createdAt);
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      })
      .reduce((sum, it) => sum + it.amount, 0);
  })();

  return { loading, items, totalThisMonth, refresh: fetchItems };
}